import { AntDesign } from "@expo/vector-icons";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { router } from "expo-router";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { View, ScrollView, Pressable } from "react-native";
import Toast from "react-native-toast-message";
import { useDispatch } from "react-redux";

import Logo from "@/assets/logo.svg";
import Copyright from "@/components/Copyright";
import Loader from "@/components/Loader";
import ProgressBarComponent from "@/components/ProgressBarComponent";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import ButtonLogout from "@/components/button/ButtonLogout";
import InputDate from "@/components/input/InputDate";
import InputDropList from "@/components/input/InputDropList";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { sendRequest } from "@/utils/ApiUtil";
import { CustomerDetailsCorporationObject } from "@/utils/dto/application.request";

type FormData = {
  countryOfIncorporation: string;
  stateOfIncorporation: string;
  establishedOn: string;
};

interface CountryData {
  country: string;
  translation: string;
  code: string;
  svg: string;
  idd: string;
}

interface StateData {
  state: string;
  stateCode: string;
  countryCode: string;
}

export default function Localization() {
  const [searchTerm, setSearchTerm] = useState("");
  const [stateSearchTerm, setStateSearchTerm] = useState("");
  const [countries, setCountries] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [states, setStates] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const globalValues = useAppSelector((state: any) => state);

  const resolver = classValidatorResolver(CustomerDetailsCorporationObject, {
    validator: { skipMissingProperties: true },
  });

  const { control, handleSubmit, formState, setValue, setFocus } =
    useForm<FormData>({
      mode: "onSubmit",
      resolver,
    });

  const { isSubmitting } = formState;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/countries", {
          searchTerm,
        });
        const mappedCountries = response.map((country: CountryData) => ({
          value: country.country,
          code: country.code,
          uniqueKey: `country-${country.code}`,
        }));
        setCountries(mappedCountries);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    if (searchTerm) {
      fetchData();
    }
  }, [searchTerm]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/states", {
          countryCode: globalValues.countryOfIncorporationAcronym,
          searchTerm: stateSearchTerm,
        });
        const mappedStates = response.map((state: StateData) => ({
          value: state.state,
          code: state.stateCode,
          uniqueKey: `state-${state.stateCode}-${state.state}`,
        }));
        setStates(mappedStates);
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };
    if (stateSearchTerm) {
      fetchData();
    }
  }, [stateSearchTerm]);

  const onSubmit = async (data: FormData) => {
    if (
      data.countryOfIncorporation === "US" &&
      data.stateOfIncorporation.length > 2
    ) {
      Toast.show({
        type: "error",
        autoHide: false,
        props: {
          text1: t("common:toast.error"),
          text2: t("common:toast.subtitle.statesus"),
        },
      });
      return;
    }
    dispatch(
      setForm({
        countryOfIncorporation: globalValues.countryOfIncorporationAcronym,
        stateOfIncorporation: data.stateOfIncorporation,
        establishedOn: data.establishedOn,
      })
    );
    router.push("/corporation/description");
  };

  const handleCountry = (data: { value: string; code: string }) => {
    dispatch(setForm({ countryOfIncorporation: data.value }));
    dispatch(setForm({ countryOfIncorporationAcronym: data.code }));
    setValue("countryOfIncorporation", data.code, { shouldValidate: true });
    setFocus("stateOfIncorporation");
    setCountries([]);
  };

  const handleState = (data: { value: string; code: string }) => {
    if (globalValues.countryOfIncorporationAcronym === "US") {
      setValue("stateOfIncorporation", data.code, { shouldValidate: true });
    } else {
      setValue("stateOfIncorporation", data.value, { shouldValidate: true });
    }
    setFocus("establishedOn");
    setStates([]);
  };

  return (
    <ScrollView className="grow bg-white1">
      <View className="z-50 absolute top-0 right-0 m-8">
        <ButtonLogout />
      </View>
      <View className="items-center justify-center w-full h-full">
        <Logo className="m-2" />
        <View className="m-2">
          <Title text={t("onboarding/corporation/localization:title")} />
        </View>
        <View className="items-center h-fit w-full lg:w-3/5 lg:h-152 lg:border lg:border-solid lg:border-gray lg:rounded-md lg:shadow">
          <Pressable
            onPress={() => {
              router.back();
            }}
            className="absolute z-[60] top-0 left-4 lg:left-6 lg:top-10"
          >
            <AntDesign name="arrowleft" size={24} color="#BDBDBD" />
          </Pressable>
          <View className="w-4/5 items-center mb-1 lg:w-full">
            <ProgressBarComponent progress={0} animatedValue={0.25} />
          </View>
          <View className="flex-grow justify-center w-full items-center">
            <View className="z-50">
              <InputDropList
                label={t(
                  "onboarding/corporation/localization:input.countryOfIncorporation"
                )}
                handleChange={(data) => {
                  handleCountry(data);
                }}
                clearValueFunction={() => {
                  setCountries([]);
                }}
                control={control}
                name="countryOfIncorporation"
                data={countries}
                onSearchChange={(term) => setSearchTerm(term)}
                placeholder={t("common:placeholder.country")}
              />
            </View>
            <View className="z-40">
              <InputDropList
                name="stateOfIncorporation"
                label={t(
                  "onboarding/corporation/localization:input.stateOfIncorporation"
                )}
                handleChange={(data) => {
                  handleState(data);
                }}
                clearValueFunction={() => {
                  setStates([]);
                }}
                control={control}
                placeholder={t(
                  "onboarding/corporation/localization:placeholder.state"
                )}
                onSearchChange={(term) => setStateSearchTerm(term)}
                data={states}
              />
            </View>
            <InputDate
              label={t(
                "onboarding/corporation/localization:input.establishedOn"
              )}
              control={control}
              name="establishedOn"
            />
          </View>
          <View className="w-full items-center">
            <Button
              label={t("common:button.nextstep")}
              onClick={handleSubmit(onSubmit)}
            />
          </View>
        </View>
        <View className="flex-grow justify-end">
          <Copyright />
        </View>
      </View>
      {isSubmitting && <Loader />}
    </ScrollView>
  );
}
